import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { apiEditor } from 'services/api/api';
import { FiltroRegistro } from 'utils/types/filtroRegistro';
import { IModificadorCategoria } from 'utils/types/modificadorCategoria';
import { IRegistroExibicao } from 'utils/types/registroExibicao';

import { AddCircle, Error, Search } from '@mui/icons-material';
import { Box, Tab, Tooltip, Typography } from '@mui/material';
import { Grid } from '@mui/system';

import AddEditRegistrosModal from 'components/AddEditRegistrosModal';
import DynamicDataTable from 'components/DynamicDataTable';
import FilterRegistroModal from 'components/FilterRegistroModal';

import { getCamposPorRegistro } from './tabsNavigation.service';
import {
  ContainerLoadingTabela,
  Loading,
  StyledTabs,
  TabContainer,
} from './TabsNavigation.styles';

interface TabsNavigationProps {
  dadosAbas: IRegistroExibicao[];
  processamentoSpedId: string;
  filtroPorRegistro: FiltroRegistro[];
  setFiltroPorRegistro: (value: FiltroRegistro[]) => void;
  paiLoaderId: number;
  modificadoresCategoriaBloco?: IModificadorCategoria[];
  modificadoresCategoriaRegistro?: IModificadorCategoria[];
  categoriaBlocoId?: number;
}

const initialValues = {
  pagina: 1,
  itensPorPagina: 10,
};

const TabsNavigation: React.FC<TabsNavigationProps> = ({
  dadosAbas,
  processamentoSpedId,
  filtroPorRegistro,
  setFiltroPorRegistro,
  paiLoaderId,
  modificadoresCategoriaBloco,
  modificadoresCategoriaRegistro,
  categoriaBlocoId,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPaiLoaderID, setSelectedPaiLoaderID] = useState(0);

  const [pagina, setPagina] = useState(initialValues.pagina);
  const [itensPorPagina, setItensPorPagina] = useState(
    initialValues.itensPorPagina,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const handleFilterRegistroModal = () => setModalOpen(!modalOpen);

  const [modalAberto, setModalAberto] = useState(false);
  const [modoFormulario, setModoFormulario] = useState<'add' | 'edit'>('add');
  const [registroSelecionadoIndex, setRegistroSelecionadoIndex] = useState<
    number | null
  >(null);

  const [filhosTabs, setFilhosTabs] = useState<
    Record<string, IRegistroExibicao[]>
  >({});

  if (
    !!modificadoresCategoriaRegistro &&
    modificadoresCategoriaRegistro.length > 0
  ) {
    const dadosAbasModificador: IRegistroExibicao[] = [];
    dadosAbas.forEach((dadosRegistro: IRegistroExibicao) => {
      modificadoresCategoriaRegistro.forEach(
        (modificadorCategoria: IModificadorCategoria) => {
          dadosAbasModificador.push({
            id: dadosRegistro.id,
            codigo: dadosRegistro.codigo,
            descricao: modificadorCategoria.descricao,
            possuiRegistroFilho: dadosRegistro.possuiRegistroFilho,
            modificadorId: modificadorCategoria.id,
          });
        },
      );
    });
    dadosAbas = dadosAbasModificador;
  }

  const abaAtual: IRegistroExibicao = dadosAbas[selectedTab];
  const filtroRegistroAtual = filtroPorRegistro.find(
    (filtro) => filtro.codigoRegistro === abaAtual?.codigo,
  );

  const {
    data: dadosCamposPorRegistro,
    isPending: isPendingCamposPorRegistro,
    refetch: refetchCamposPorRegistro,
  } = useQuery({
    queryKey: [
      'camposPorRegistro',
      processamentoSpedId,
      abaAtual?.codigo,
      abaAtual?.modificadorId,
      paiLoaderId,
      pagina,
      itensPorPagina,
      filtroRegistroAtual?.filtroLinha,
      modificadoresCategoriaBloco,
      modificadoresCategoriaRegistro,
    ],
    queryFn: () =>
      getCamposPorRegistro(
        abaAtual?.codigo,
        paiLoaderId,
        pagina,
        itensPorPagina,
        processamentoSpedId,
        filtroRegistroAtual?.filtroLinha,
        modificadoresCategoriaBloco?.map((modificador) => modificador.id),
        modificadoresCategoriaRegistro
          ?.filter(
            (modificador) =>
              !!abaAtual?.modificadorId &&
              modificador.id === abaAtual?.modificadorId,
          )
          ?.map((modificador) => modificador.id),
      ),
    enabled: !!abaAtual?.codigo,
    staleTime: 1000 * 60 * 10,
  });

  const handleChangeSelectedTab = (
    _: React.SyntheticEvent,
    newValue: number,
  ) => {
    setFiltroPorRegistro(
      filtroPorRegistro.filter(
        (filtro) => filtro.codigoRegistro !== dadosAbas[selectedTab]?.codigo,
      ),
    );
    setSelectedTab(newValue);
    setFilhosTabs({});
    resetPagination();
  };

  const getFiltroAplicadoAbaSelecionada = (): FiltroRegistro | undefined => {
    return filtroPorRegistro.find(
      (filtro) => filtro.codigoRegistro === dadosAbas[selectedTab]?.codigo,
    );
  };

  const atualizarFiltroPorRegistro = (
    filtroAplicadoAbaSelecionada: FiltroRegistro,
    lineNumber: number | undefined,
    filtroMaisRecente?: FiltroRegistro[],
  ) => {
    const filtroPorRegistroAtual = filtroMaisRecente || filtroPorRegistro;
    var filtroPorRegistroAtualizado = filtroPorRegistroAtual.filter(
      (filtroAtual) => {
        return (
          filtroAplicadoAbaSelecionada.codigoRegistro !==
          filtroAtual.codigoFiltroPai
        );
      },
    );

    if (lineNumber === undefined) {
      filtroPorRegistroAtualizado = filtroPorRegistroAtualizado.filter(
        (filtroAtual) =>
          filtroAplicadoAbaSelecionada.codigoRegistro !==
          filtroAtual.codigoRegistro,
      );
    }

    return filtroPorRegistroAtualizado.map((filtro) =>
      filtro.codigoRegistro === filtroAplicadoAbaSelecionada?.codigoRegistro
        ? { ...filtro, filtroLinha: lineNumber }
        : filtro,
    );
  };

  const filtrarLinhaRegistro = (lineNumber: number | null) => {
    if (lineNumber === null) {
      return;
    }
    resetPagination();

    var filtroAplicadoAbaSelecionada: FiltroRegistro | undefined =
      getFiltroAplicadoAbaSelecionada();
    var filtroPorRegistroAtualizado: FiltroRegistro[] = [];

    if (
      filtroAplicadoAbaSelecionada &&
      filtroAplicadoAbaSelecionada.filtroLinha !== lineNumber
    ) {
      filtroPorRegistroAtualizado = atualizarFiltroPorRegistro(
        filtroAplicadoAbaSelecionada,
        lineNumber,
      );
      setFiltroPorRegistro(filtroPorRegistroAtualizado);
    } else if (!filtroAplicadoAbaSelecionada) {
      const abaAtual = dadosAbas[selectedTab];
      const novoFiltro: FiltroRegistro = {
        codigoRegistro: abaAtual?.codigo,
        filtroLinha: lineNumber,
        codigoFiltroPai: abaAtual?.codigoRegistroPai,
      };
      filtroPorRegistroAtualizado = [...filtroPorRegistro, novoFiltro];
      filtroPorRegistroAtualizado = atualizarFiltroPorRegistro(
        novoFiltro,
        lineNumber,
        filtroPorRegistroAtualizado,
      );
      setFiltroPorRegistro(filtroPorRegistroAtualizado);
    }
    setFilhosTabs({});
  };

  const limparFiltroLinhaRegistro = () => {
    var filtroAplicadoAbaSelecionada: FiltroRegistro | undefined =
      getFiltroAplicadoAbaSelecionada();

    if (
      filtroAplicadoAbaSelecionada &&
      filtroAplicadoAbaSelecionada.filtroLinha !== undefined
    ) {
      const filtroPorRegistroAtualizado = atualizarFiltroPorRegistro(
        filtroAplicadoAbaSelecionada,
        undefined,
      );
      setFiltroPorRegistro(filtroPorRegistroAtualizado);
    }
    resetPagination();
    setFilhosTabs({});
  };

  const resetPagination = () => {
    setPagina(initialValues.pagina);
    setItensPorPagina(initialValues.itensPorPagina);
  };

  const handleEditarRegistro = (registroIndex: number) => {
    setModoFormulario('edit');
    setRegistroSelecionadoIndex(registroIndex);
    setModalAberto(true);
  };

  const handleAdicionarRegistro = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModoFormulario('add');
    setRegistroSelecionadoIndex(null);
    setModalAberto(true);
  };

  const handleCloseAddEditModal = () => {
    setModalAberto(false);
  };

  return (
    <>
      <FilterRegistroModal
        codigoRegistro={dadosAbas[selectedTab]?.codigo}
        open={modalOpen}
        onClose={handleFilterRegistroModal}
        searchButtonAction={filtrarLinhaRegistro}
        clearFilterButtonAction={limparFiltroLinhaRegistro}
      />
      <TabContainer>
        <StyledTabs
          value={selectedTab}
          onChange={handleChangeSelectedTab}
          variant="scrollable"
          scrollButtons="auto"
        >
          {dadosAbas.map((aba: any, index: number) => (
            <Tab
              key={aba.id}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {`${aba.descricao} (${aba.codigo})`}
                  {selectedTab === index && (
                    <>
                      <Tooltip title="Adicionar Registro">
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '2px',
                          }}
                          onClick={handleAdicionarRegistro}
                        >
                          <AddCircle color="success" />
                        </Box>
                      </Tooltip>
                      <Tooltip title="Buscar Linha">
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '2px',
                          }}
                          onClick={handleFilterRegistroModal}
                        >
                          <Search color="secondary" />
                        </Box>
                      </Tooltip>
                    </>
                  )}
                </Box>
              }
            />
          ))}
        </StyledTabs>
      </TabContainer>

      {isPendingCamposPorRegistro ? (
        <ContainerLoadingTabela>
          <Loading />
        </ContainerLoadingTabela>
      ) : !!dadosCamposPorRegistro?.valores.conteudo.length ? (
        <DynamicDataTable
          processamentoSpedId={processamentoSpedId}
          dadosCamposPorRegistro={dadosCamposPorRegistro}
          registroCodigo={dadosAbas[selectedTab]?.codigo}
          onEditRegistro={handleEditarRegistro}
          onDeleteRegistro={(id: number) => {
            if (selectedPaiLoaderID === id) {
              setSelectedPaiLoaderID(0);
              setFilhosTabs({});
            }
          }}
          registroId={dadosAbas[selectedTab]?.id}
          possuiRegistroFilho={
            dadosAbas[selectedTab]?.possuiRegistroFilho ?? false
          }
          onPageChange={(page) => setPagina(page)}
          onRowsPerPageChange={(rows) => {
            setItensPorPagina(rows);
            setPagina(initialValues.pagina);
          }}
          modo={modoFormulario}
          isModalAberto={modalAberto}
          setIsModalAberto={setModalAberto}
          registroSelecionadoIndex={registroSelecionadoIndex}
          refetchCamposPorRegistro={refetchCamposPorRegistro}
          onVisualizarRegistrosFilhos={async (registroId, registroLoaderId) => {
            const response = await apiEditor.get(
              `/registros/registros-filhos`,
              {
                params: {
                  registroPaiExibicaoId: registroId,
                  categoriaBlocoId,
                },
              },
            );

            if (response.data?.length > 0) {
              const filhos = response.data.map((filho: IRegistroExibicao) => ({
                id: filho.id,
                codigo: filho.codigo,
                descricao: `${filho.descricao}`,
                codigoRegistroPai: dadosAbas[selectedTab]?.codigo,
                possuiRegistroFilho: filho.possuiRegistroFilho,
              }));

              setFilhosTabs((prev) => ({
                ...prev,
                [dadosAbas[selectedTab]?.codigo]: filhos,
              }));

              setSelectedPaiLoaderID(registroLoaderId);
            }
          }}
        />
      ) : (
        <Grid size={12} container justifyContent="center" mt={3}>
          <Typography
            variant="body1"
            style={{ fontSize: '16px', display: 'flex' }}
            color="error"
          >
            <Error fontSize="medium" style={{ marginRight: 4 }} color="error" />
            Registro não localizado no arquivo!
          </Typography>
        </Grid>
      )}

      {modalAberto && dadosCamposPorRegistro && (
        <AddEditRegistrosModal
          isOpen={modalAberto}
          onClose={handleCloseAddEditModal}
          dadosCamposPorRegistro={dadosCamposPorRegistro}
          registroCodigo={dadosAbas[selectedTab]?.codigo}
          processamentoSpedId={processamentoSpedId}
          modo={modoFormulario}
          registroSelecionadoIndex={registroSelecionadoIndex}
          refetchCamposPorRegistro={refetchCamposPorRegistro}
          paiLoaderId={paiLoaderId}
          modificadoresCategoriaBloco={modificadoresCategoriaBloco}
          modificadoresCategoriaRegistro={modificadoresCategoriaRegistro}
          modificadorRegistroAbaAtual={dadosAbas[selectedTab]?.modificadorId}
        />
      )}

      {Object.entries(filhosTabs).map(([paiId, filhos]) => (
        <TabsNavigation
          key={paiId}
          dadosAbas={filhos}
          processamentoSpedId={processamentoSpedId}
          filtroPorRegistro={filtroPorRegistro}
          setFiltroPorRegistro={setFiltroPorRegistro}
          paiLoaderId={selectedPaiLoaderID}
          categoriaBlocoId={categoriaBlocoId}
        />
      ))}
    </>
  );
};

export default TabsNavigation;
